import { createContext } from 'react';
import {
	IUpdateModifierRestaurantProperties,
	IRestaurantPropertiesModifierGroup,
	IItemForGetShortInfoWithStatus,
	IServerError,
	ICircularReferenceValidationError,
	TPortionSelectionMode,
} from 'types';
import { defaultPortionSelectionMode } from 'appConstants/dishes';

export type TTypeOfModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'toConfirmSubmit'
	| 'saveAsPatch'
	| 'success'
	| 'error';

export interface IEditModifierRestaurantPropertiesState {
	needRefetchRestPropsData: boolean;
	customerName: string;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	IDSelectedRestaurant: Nullable<string>;
	modifierId: string;
	modifierName: string;
	price: Nullable<number>;
	supplementPrice: Nullable<number>;
	isCreatedFromPos: boolean;
	inStop: boolean;
	isRestricted: boolean;
	isPreselected: boolean;
	forceDisable: boolean;
	quantityMaxLimit: Nullable<number>;
	parentModifierGroups: {
		id: string;
		name: string;
		isDefault: boolean;
		isAssignedToPortion: boolean;
	}[];
	hasSchedulePriority: boolean;
	activationSchedule: string;
	deactivationSchedule: string;
	IDsSelectedModifierGroups: string[];
	modifierGroupsWithAdditionalData: IRestaurantPropertiesModifierGroup[];
	hasPortions: boolean;
	portionSelectionMode: TPortionSelectionMode;
	hasPortionBasedNestedModGroups: boolean;
	dataForSubmit: Nullable<IUpdateModifierRestaurantProperties>;
	typeOfModalWindow: TTypeOfModalWindow;
	errorResponseData: Nullable<IServerError<ICircularReferenceValidationError>>;
}

export const editModifierRestaurantPropertiesInitialState: IEditModifierRestaurantPropertiesState = {
	needRefetchRestPropsData: false,
	customerName: '',
	fetchedRestaurants: [],
	IDSelectedRestaurant: null,
	modifierId: '',
	modifierName: '',
	price: null,
	supplementPrice: null,
	isCreatedFromPos: false,
	inStop: false,
	isRestricted: false,
	isPreselected: false,
	forceDisable: false,
	quantityMaxLimit: null,
	parentModifierGroups: [],
	hasSchedulePriority: false,
	activationSchedule: '',
	deactivationSchedule: '',
	IDsSelectedModifierGroups: [],
	modifierGroupsWithAdditionalData: [],
	hasPortions: false,
	portionSelectionMode: defaultPortionSelectionMode,
	hasPortionBasedNestedModGroups: false,
	dataForSubmit: null,
	typeOfModalWindow: null,
	errorResponseData: null,
};

export interface IEditModifierRestaurantPropertiesContext extends IEditModifierRestaurantPropertiesState {
	refetchRestPropsData(value: boolean): void;
	setIDRestaurant(value: Nullable<string>): void;
	changePrice(value: Nullable<number>): void;
	changeSupplementPrice(value: Nullable<number>): void;
	changeQuantityMaxLimit(value: Nullable<number>): void;
	toggleInStop(): void;
	toggleIsRestricted(): void;
	toggleIsPreselected(): void;
	toggleForceDisable(): void;
	changePortionSelectionMode(value: TPortionSelectionMode): void;
	changeActivationSchedule(value: string): void;
	changeDeactivationSchedule(value: string): void;
	setModifierGroups(value: string[]): void;
	setModifierGroupsWithAdditionalData(value: IRestaurantPropertiesModifierGroup[]): void;
	setDataForSubmit(data: IUpdateModifierRestaurantProperties): void;
	changeTypeOfModalWindow(value: TTypeOfModalWindow): void;
	showErrorModalWithInfo(value: Nullable<IServerError<ICircularReferenceValidationError>>): void;
}

export const EditModifierRestaurantPropertiesContext = createContext<IEditModifierRestaurantPropertiesContext>({
	refetchRestPropsData: () => undefined,
	setIDRestaurant: () => undefined,
	changePrice: () => undefined,
	changeSupplementPrice: () => undefined,
	changeQuantityMaxLimit: () => undefined,
	toggleInStop: () => undefined,
	toggleIsRestricted: () => undefined,
	toggleIsPreselected: () => undefined,
	toggleForceDisable: () => undefined,
	changePortionSelectionMode: () => undefined,
	changeActivationSchedule: () => undefined,
	changeDeactivationSchedule: () => undefined,
	setModifierGroups: () => undefined,
	setModifierGroupsWithAdditionalData: () => undefined,
	setDataForSubmit: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	showErrorModalWithInfo: () => undefined,
	...editModifierRestaurantPropertiesInitialState,
});
